import { useQuery } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import type { MessageKey } from "../../i18n/dictionaries";
import type { LineCode } from "./use-prepare-offers";

/** One distributable package option (GET /v1/catalog/packages?line= row). The
 *  list is distribution-gated + org-scoped server-side (the advisor dropdown). */
export interface CatalogPackage {
  packageCode: string;
  carrierTradeName: string;
  packageName: string;
  lineCode: string;
}

/** GET /v1/catalog/packages?line=<lineCode> — the "Şirket — Paket" advisor
 *  dropdown source. Returns the standard Page envelope; re-fetched per line
 *  (the key carries lineCode). catalog:read-gated server-side. */
export function useCatalogPackages(lineCode: LineCode) {
  const api = useApi();
  return useQuery({
    queryKey: ["catalog-packages", lineCode],
    queryFn: () =>
      api.get<Page<CatalogPackage>>(`/v1/catalog/packages?line=${encodeURIComponent(lineCode)}`),
  });
}

export interface PackagePickerProps {
  lineCode: LineCode;
  value: string;
  onChange: (packageCode: string) => void;
  ariaLabel?: string;
  disabled?: boolean;
}

/** PackagePicker — given a line, fetches the distributable packages and renders a
 *  Select labeled "{carrierTradeName} — {packageName}" (value = packageCode). If
 *  the list is EMPTY (or the read fails) it degrades to the existing free-text
 *  packageCode input so the advisor is never blocked. Exported so the quote-form
 *  (built separately) can reuse it. */
export function PackagePicker({
  lineCode,
  value,
  onChange,
  ariaLabel,
  disabled,
}: PackagePickerProps) {
  const { t } = useT();
  const { data, isLoading, isError } = useCatalogPackages(lineCode);
  const label = ariaLabel ?? t("po.packageCode");
  const packages = data?.data ?? [];

  // Free-text fallback — an empty list (this line has no distributable package
  // yet) or a read error must never wedge authoring; the server resolves the
  // carrier from the typed code and enforces the distribution gate regardless.
  const freeText = (note: MessageKey, tone: "hint" | "error") => (
    <>
      <input
        className="k-input"
        aria-label={label}
        value={value}
        placeholder={t("po.packagePlaceholder")}
        disabled={disabled}
        data-testid="pp-freetext"
        onChange={(e) => onChange(e.target.value)}
      />
      <span
        className={tone === "error" ? "k-field__error" : "k-field__hint"}
        data-testid="pp-fallback-note"
      >
        <Icon name="info" size={14} /> {t(note)}
      </span>
    </>
  );

  if (isLoading) {
    return (
      <span className="k-select" aria-busy="true" data-testid="pp-loading">
        <select className="k-select__native" disabled aria-label={t("po.pkg.loading")}>
          <option>{t("po.pkg.loading")}</option>
        </select>
        <span className="k-select__chevron">
          <Icon name="chevron-down" size={16} />
        </span>
      </span>
    );
  }

  if (isError) return freeText("po.pkg.errorFallback", "error");
  if (packages.length === 0) return freeText("po.pkg.emptyFallback", "hint");

  return (
    <span className="k-select" data-testid="pp-select">
      <select
        className="k-select__native"
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{t("po.pkg.placeholder")}</option>
        {packages.map((p) => (
          <option key={p.packageCode} value={p.packageCode}>
            {p.carrierTradeName} — {p.packageName}
          </option>
        ))}
      </select>
      <span className="k-select__chevron">
        <Icon name="chevron-down" size={16} />
      </span>
    </span>
  );
}
